import React, { useRef, useEffect, useState } from 'react';

const PREPOSITIONS = new Set([
    'и', 'с', 'со', 'в', 'во', 'на', 'по', 'к', 'ко', 'от', 'ото',
    'до', 'за', 'над', 'под', 'об', 'обо', 'о', 'при', 'через',
    'для', 'без', 'безо', 'из', 'изо', 'у', 'а', 'но', 'да',
    'или', 'же', 'ли', 'то', 'бы', 'ни', 'из-за', 'из-под',
]);

function isPreposition(word) {
    return PREPOSITIONS.has(word.replace(/[.,!?;:]+/g, '').toLowerCase());
}

function measureBreaksBeforePrepositions(text, containerWidth, computedStyles, wordBreakMode) {
    const tempDiv = document.createElement('div');
    tempDiv.style.position = 'absolute';
    tempDiv.style.visibility = 'hidden';
    tempDiv.style.top = '-9999px';
    tempDiv.style.left = '-9999px';
    tempDiv.style.width = `${containerWidth}px`;
    tempDiv.style.fontSize = computedStyles.fontSize;
    tempDiv.style.fontFamily = computedStyles.fontFamily;
    tempDiv.style.fontWeight = computedStyles.fontWeight;
    tempDiv.style.lineHeight = computedStyles.lineHeight;
    tempDiv.style.letterSpacing = computedStyles.letterSpacing;
    tempDiv.style.wordBreak = wordBreakMode;

    const words = text.split(/(\s+)/);
    const spans = words.map((word) => {
        const span = document.createElement('span');
        span.style.display = 'inline';
        span.textContent = word;
        tempDiv.appendChild(span);
        return span;
    });

    document.body.appendChild(tempDiv);

    const breakBefore = new Set();

    for (let i = 0; i < spans.length; i++) {
        if (!isPreposition(words[i])) continue;

        let nextWordIdx = i + 1;
        while (nextWordIdx < spans.length && words[nextWordIdx].trim() === '') {
            nextWordIdx++;
        }

        if (nextWordIdx >= spans.length) continue;

        if (spans[nextWordIdx].offsetTop > spans[i].offsetTop) {
            breakBefore.add(i);
        }
    }

    document.body.removeChild(tempDiv);
    return breakBefore;
}

function buildTokens(title, titleBreaks, caption, captionBreaks, mode) {
    const tokens = [];

    const pushText = (text, breaks) => {
        const words = text.split(/(\s+)/);
        words.forEach((word, i) => {
            if (breaks.has(i)) tokens.push({ type: 'br' });
            tokens.push({ type: 'text', value: word });
        });
    };

    pushText(title, titleBreaks);

    if (mode === 'split') {
        tokens.push({ type: 'br' });
    } else {
        tokens.push({ type: 'text', value: ' ' });
    }

    pushText(caption, captionBreaks);

    return tokens;
}

function findOverflowStart(tokens, containerWidth, computedStyles, numberOfLines) {
    const lineHeight = parseFloat(computedStyles.lineHeight);
    if (!Number.isFinite(lineHeight) || !numberOfLines) return -1;

    const tempDiv = document.createElement('div');
    tempDiv.style.position = 'absolute';
    tempDiv.style.visibility = 'hidden';
    tempDiv.style.top = '-9999px';
    tempDiv.style.left = '-9999px';
    tempDiv.style.width = `${containerWidth}px`;
    tempDiv.style.fontSize = computedStyles.fontSize;
    tempDiv.style.fontFamily = computedStyles.fontFamily;
    tempDiv.style.fontWeight = computedStyles.fontWeight;
    tempDiv.style.lineHeight = computedStyles.lineHeight;
    tempDiv.style.letterSpacing = computedStyles.letterSpacing;
    tempDiv.style.wordBreak = 'break-word';

    const spansByTokenIdx = [];

    tokens.forEach((token, i) => {
        if (token.type === 'br') {
            tempDiv.appendChild(document.createElement('br'));
            return;
        }
        const span = document.createElement('span');
        span.style.display = 'inline';
        span.textContent = token.value;
        tempDiv.appendChild(span);
        spansByTokenIdx.push({ i, span });
    });

    document.body.appendChild(tempDiv);

    const overflowLine = lineHeight * numberOfLines;
    let overflowStart = -1;

    spansByTokenIdx.forEach(({ i, span }) => {
        if (overflowStart === -1 && span.offsetTop >= overflowLine - 1) {
            overflowStart = i;
        }
    });

    document.body.removeChild(tempDiv);
    return overflowStart;
}

function renderTokens(tokens, overflowStart) {
    return tokens.map((token, i) => {
        if (token.type === 'br') return <br key={i} />;

        const isBreakAll = overflowStart >= 0 && i >= overflowStart;

        return (
            <span
                key={i}
                style={isBreakAll ? { wordBreak: 'break-all' } : undefined}
            >
                {token.value}
            </span>
        );
    });
}

export const TwoLineText = ({
    title, caption, titleClass, numberOfLines,
}) => {
    const containerRef = useRef(null);
    const [mode, setMode] = useState('auto');
    const [titleBreaks, setTitleBreaks] = useState(new Set());
    const [captionBreaks, setCaptionBreaks] = useState(new Set());
    const [overflowStart, setOverflowStart] = useState(-1);

    useEffect(() => {
        const checkLayout = () => {
            if (!containerRef.current) return;

            const container = containerRef.current;
            const containerWidth = container.clientWidth;
            if (containerWidth === 0) return;

            const cs = getComputedStyle(container);

            const tempDiv = document.createElement('div');
            tempDiv.style.position = 'absolute';
            tempDiv.style.visibility = 'hidden';
            tempDiv.style.top = '-9999px';
            tempDiv.style.left = '-9999px';
            tempDiv.style.width = `${containerWidth}px`;
            tempDiv.style.fontSize = cs.fontSize;
            tempDiv.style.fontFamily = cs.fontFamily;
            tempDiv.style.fontWeight = cs.fontWeight;
            tempDiv.style.lineHeight = cs.lineHeight;
            tempDiv.style.letterSpacing = cs.letterSpacing;
            tempDiv.style.wordBreak = 'break-word';

            const titleSpan = document.createElement('span');
            titleSpan.style.display = 'inline';
            titleSpan.textContent = title;
            tempDiv.appendChild(titleSpan);

            document.body.appendChild(tempDiv);

            const titleHeight = titleSpan.offsetHeight;
            const lineHeight = parseFloat(cs.lineHeight);

            document.body.removeChild(tempDiv);

            const newMode = titleHeight <= lineHeight * 1.2 ? 'split' : 'merged';
            setMode(newMode);

            let newTitleBreaks;
            let newCaptionBreaks;
            const wb = 'break-word';

            if (newMode === 'split') {
                newTitleBreaks = measureBreaksBeforePrepositions(title, containerWidth, cs, wb);
                newCaptionBreaks = measureBreaksBeforePrepositions(caption, containerWidth, cs, wb);
            } else {
                const combined = `${title} ${caption}`;
                const combinedBreaks = measureBreaksBeforePrepositions(combined, containerWidth, cs, wb);

                const titleParts = title.split(/(\s+)/);
                newTitleBreaks = new Set();
                newCaptionBreaks = new Set();

                for (const idx of combinedBreaks) {
                    if (idx < titleParts.length) {
                        newTitleBreaks.add(idx);
                    } else {
                        const capIdx = idx - titleParts.length - 1;
                        if (capIdx >= 0) newCaptionBreaks.add(capIdx);
                    }
                }
            }

            setTitleBreaks(newTitleBreaks);
            setCaptionBreaks(newCaptionBreaks);

            const tokens = buildTokens(title, newTitleBreaks, caption, newCaptionBreaks, newMode);
            setOverflowStart(findOverflowStart(tokens, containerWidth, cs, numberOfLines));
        };

        const timeoutId = setTimeout(checkLayout, 10);
        const resizeObserver = new ResizeObserver(() => {
            setTimeout(checkLayout, 10);
        });

        if (containerRef.current) {
            resizeObserver.observe(containerRef.current);
        }

        return () => {
            clearTimeout(timeoutId);
            resizeObserver.disconnect();
        };
    }, [title, caption, numberOfLines]);

    const tokens = buildTokens(title, titleBreaks, caption, captionBreaks, mode);

    return (
        <div
            ref={containerRef}
            className={titleClass}
            style={{
                display: '-webkit-box',
                WebkitLineClamp: numberOfLines,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                wordBreak: 'break-word',
            }}
        >
            {renderTokens(tokens, overflowStart)}
        </div>
    );
};
